// THE CLIENT SIDE OF ORGANISATION DEDUPE (P1: the finder proposes, the operator decides).
//
// The finder never merges anything. It files a `dedupe` proposal — a pair, an action, a survivor
// and the reasons it believes them to be one organisation — and that proposal is held, amended or
// refused in the review queue like every other claim. Nothing here writes to the record; the two
// calls below are (1) "look for pairs" and (2) "what did you find".
//
// The SHAPES live in `src/shared/types.ts`, which both sides read; this file adds only what the
// console needs on top of them: the words for the three actions and the six reasons, and the
// readers that open a generic proposal payload without trusting it.
//
// Vocabulary, as everywhere: unheld · hold · held · amended · refused. A dismissal is not a
// refusal: refusing says "this filing is wrong", dismissing says "these two are genuinely
// different organisations, never propose them again" — and the second is a HELD decision.
import { request } from './client.js';
import type {
  DedupeAction, DedupeCandidate, DedupeCard, DedupePair, DedupePayload, DedupeReasonKind,
  DedupeRunResult, ListResponse,
} from '../../shared/types.js';

export type {
  DedupeAction, DedupeCandidate, DedupeCard, DedupePair, DedupePayload, DedupeReason,
  DedupeReasonKind, DedupeRunResult,
} from '../../shared/types.js';

/** The three, in the order the amend control offers them. */
export const DEDUPE_ACTIONS: DedupeAction[] = ['merge', 'relate', 'dismiss'];

/** English label per action — the i18n key, translated at the call site with `t()`. */
export const DEDUPE_ACTION_WORD: Record<DedupeAction, string> = {
  merge: 'merge',
  relate: 'relate',
  dismiss: 'not a duplicate',
};

/** English label per reason. A score is not a reason, so a score has no entry here. */
export const DEDUPE_REASON_WORD: Record<DedupeReasonKind, string> = {
  name_similarity: 'the names are near-identical',
  shared_domain: 'they share a domain',
  shared_uid: 'they carry the same UID',
  shared_people: 'the same people are affiliated to both',
  alias_match: 'one name is already an alias of the other',
  punctuation_only: 'only punctuation and case differ',
};

const asRecord = (v: unknown): Record<string, unknown> =>
  (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : {});

const asNumber = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

/** Is this one of the three actions? An unknown string is not coerced into `merge`. */
export function isDedupeAction(v: unknown): v is DedupeAction {
  return v === 'merge' || v === 'relate' || v === 'dismiss';
}

/**
 * A proposal payload, read as a dedupe payload. Returns null when the shape is not one — a
 * malformed filing renders as an ordinary proposal instead of pretending to be a pair. The queue
 * carries payloads as `Record<string, unknown>`, and an amended one has been through a text
 * input, so nothing here is assumed and everything is checked.
 */
export function dedupePayloadOf(payload: unknown): DedupePayload | null {
  const p = asRecord(payload);
  const a = asNumber(p.a_id);
  const b = asNumber(p.b_id);
  if (a === null || b === null || !isDedupeAction(p.action)) return null;
  const reasons = Array.isArray(p.reasons)
    ? p.reasons.map((r) => {
        const row = asRecord(r);
        return {
          kind: String(row.kind ?? '') as DedupeReasonKind,
          detail: typeof row.detail === 'string' ? row.detail : null,
        };
      })
    : [];
  return {
    action: p.action,
    a_id: a,
    b_id: b,
    survivor_id: asNumber(p.survivor_id) ?? a,
    reasons,
    score: asNumber(p.score) ?? 0,
  };
}

/**
 * The pair the stand carries. It is read here and not in `api/proposals.ts` because the review
 * queue's own contract knows nothing about dedupe: the stand is a generic envelope, and this is
 * the one place that opens it.
 */
export function pairOf(stand: unknown): DedupePair | null {
  const pair = asRecord(asRecord(stand).pair);
  const a = asRecord(pair.a);
  const b = asRecord(pair.b);
  if (typeof a.id !== 'number' || typeof b.id !== 'number') return null;
  return { a: a as unknown as DedupeCard, b: b as unknown as DedupeCard };
}

/** The card for one side of the pair, by id — `null` when the pair does not name that row. */
export function sideOf(pair: DedupePair | null, id: number | null | undefined): DedupeCard | null {
  if (!pair || id === null || id === undefined) return null;
  return pair.a.id === id ? pair.a : pair.b.id === id ? pair.b : null;
}

/** The other side of the pair — for a merge, the row that gets archived. */
export function otherOf(pair: DedupePair | null, survivorId: number | null | undefined): DedupeCard | null {
  if (!pair) return null;
  return pair.a.id === survivorId ? pair.b : pair.a;
}

/** POST /api/dedupe/run — file a proposal per undecided pair. Writes nothing to the record. */
export function runDedupe(): Promise<DedupeRunResult> {
  return request<DedupeRunResult>('/dedupe/run', { method: 'POST' });
}

/** GET /api/dedupe/candidates — the pairs the finder sees, without filing anything. */
export function listDedupeCandidates(): Promise<ListResponse<DedupeCandidate>> {
  return request<ListResponse<DedupeCandidate>>('/dedupe/candidates');
}
